/** Local, decrypted data model. Never leaves the device. */
import type { AttachmentRef, MessageContent } from '@vav/protocol';

export type MessageState =
  | 'pending'
  | 'sent'
  | 'delivered'
  | 'read'
  | 'failed'
  /** Written and sealed, held by the server until its time. */
  | 'scheduled';

export interface Message {
  id: string;
  conversationId: string;
  senderId: string;
  outgoing: boolean;
  ts: number;
  content: MessageContent;
  state: MessageState;
  replyTo?: string;
  /** When a scheduled message is due to leave. */
  scheduledFor?: number;
  /** The held envelopes on the server, so the send can still be withdrawn. */
  scheduledEnvelopeIds?: string[];
  /** The text was changed after sending. Shown, because silent rewriting of a
      conversation's record is worse than an "edited" label. */
  edited?: boolean;
  /** emoji -> user ids who reacted with it. */
  reactions?: Record<string, string[]>;
  deleted?: boolean;
}

export interface Conversation {
  id: string;
  kind: 'dm' | 'group';
  /** Peer user id for a DM. */
  peerId?: string;
  groupId?: string;
  memberIds: string[];
  title?: string;
  avatar?: AttachmentRef;
  lastMessageAt: number;
  lastPreview?: string;
  unread: number;
  /**
   * Whether my profile (name and picture) has been shared into this
   * conversation at least once. Conversations from before profile sharing
   * existed lack it; the connect-time healer settles the debt.
   */
  profileShared?: boolean;
  /**
   * When a profile-request was last sent into this conversation. Throttles
   * the ask: a peer on a build too old to answer also renders the request as
   * an empty bubble, so asking on every reconnect would spam their thread.
   */
  profileRequestedAt?: number;
  /** The DM peer deleted their account; discovered on send, shown on open. */
  peerGone?: boolean;
}

export interface Contact {
  id: string;
  username: string;
  displayName: string;
  /** Their encrypted profile picture, sent by them through the sealed channel. */
  avatar?: AttachmentRef;
  /** Their Ed25519 identity key, base64. Pinned on first contact. */
  identityKey?: string;
  verified?: boolean;
  lastSeen?: number;
}

export interface Account {
  userId: string;
  deviceId: number;
  username: string;
  displayName: string;
  token: string;
  serverUrl: string;
  /** My own encrypted profile picture — the same ref my contacts hold. */
  avatar?: AttachmentRef;
}

export const STORAGE_KEYS = {
  account: 'account',
  identity: 'identity',
  /**
   * One ratchet session per *device*, not per user. A contact with a phone and
   * a laptop is two independent sessions, and conflating them would corrupt
   * both ratchets the moment the second device replied.
   */
  session: (peerUserId: string, peerDeviceId: number) => `session:${peerUserId}:${peerDeviceId}`,
  sessionPrefix: 'session:',
  conversation: (id: string) => `conv:${id}`,
  conversationPrefix: 'conv:',
  contact: (id: string) => `contact:${id}`,
  contactPrefix: 'contact:',
  /**
   * Cached discovery hashes, one blob per pepper.
   *
   * The pepper's fingerprint is in the key so that rotating it on the server
   * starts a fresh cache rather than serving hashes that can no longer match
   * anything — a stale cache would look like it was working and find nobody.
   */
  discoveryCache: (pepperFingerprint: string) => `discovery:${pepperFingerprint}`,
  /**
   * Timestamp is zero-padded so lexicographic key order matches chronological
   * order — that is what lets history load with a prefix scan and no index.
   */
  message: (conversationId: string, ts: number, id: string) =>
    `msg:${conversationId}:${String(ts).padStart(16, '0')}:${id}`,
  messagePrefix: (conversationId: string) => `msg:${conversationId}:`,
} as const;
